/**
 * Skills Page
 * 
 * Main page for managing organization skills.
 * Features:
 * - List available skills (built-in and custom)
 * - Enable/disable skills
 * - Configure skill settings
 * - View skill documentation
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import {
  listSkills,
  enableSkill,
  disableSkill,
  updateSkillConfig,
  type Skill,
} from '../api';
import {
  Button,
  Card,
  Badge,
  PageHeader,
  PageLoading,
  NoOrganizationMessage,
} from '../components';

// Skill icon mapping
const skillIcons: Record<string, string> = {
  'web-search': '🔍',
  'http-request': '🌐',
  credentials: '🔐',
  browser: '🖥️',
  default: '🛠️',
};

function getSkillIcon(skillId: string): string {
  return skillIcons[skillId] || skillIcons.default;
}

function getTypeBadgeColor(type: Skill['type']): string {
  return type === 'builtin'
    ? 'bg-blue-100 text-blue-800'
    : 'bg-purple-100 text-purple-800';
}

function getStatusBadge(enabled: boolean): React.ReactNode {
  return enabled ? (
    <Badge variant="success">Aktivoitu</Badge>
  ) : (
    <Badge variant="neutral">Ei aktivoitu</Badge>
  );
}

export default function SkillsPage() {
  const { t } = useTranslation();
  const { activeOrg } = useAuthStore();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingSkill, setTogglingSkill] = useState<string | null>(null);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  useEffect(() => {
    if (activeOrg) {
      loadSkills();
    }
  }, [activeOrg]);

  const loadSkills = async () => {
    if (!activeOrg) return;
    setLoading(true);
    setError(null);
    try {
      const response = await listSkills(activeOrg.id);
      setSkills(response.skills);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSkill = async (skill: Skill) => {
    if (!activeOrg) return;
    setTogglingSkill(skill.id);
    try {
      const isEnabled = skill.orgConfig?.enabled || false;
      if (isEnabled) {
        await disableSkill(skill.id, activeOrg.id);
      } else {
        await enableSkill(skill.id, activeOrg.id);
      }
      await loadSkills();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle skill');
    } finally {
      setTogglingSkill(null);
    }
  };

  const handleConfigure = (skill: Skill) => {
    setSelectedSkill(skill);
    setShowConfigModal(true);
  };

  if (!activeOrg) {
    return <NoOrganizationMessage />;
  }

  if (loading) return <PageLoading message="Ladataan skillejä..." />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title={`🛠️ Skillit — ${activeOrg.name}`}
        action={
          <Button onClick={loadSkills} variant="secondary">
            🔄 Päivitä
          </Button>
        }
      />

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Skills Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {skills.map((skill) => {
          const isEnabled = skill.orgConfig?.enabled || false;
          return (
            <Card key={skill.id} className="flex flex-col">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{getSkillIcon(skill.id)}</span>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">
                      {skill.name}
                    </h3>
                    <p className="text-sm text-gray-500">Versio {skill.version}</p>
                  </div>
                </div>
                <Badge className={getTypeBadgeColor(skill.type)}>
                  {skill.type === 'builtin' ? 'Sisäänrakennettu' : 'Custom'}
                </Badge>
              </div>

              <p className="text-gray-600 mb-4 flex-grow">
                {skill.description || 'Ei kuvausta saatavilla.'}
              </p>

              {/* Required tools */}
              {!!skill.manifest?.tools && Array.isArray(skill.manifest.tools) && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                    Työkalut
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {(skill.manifest.tools as string[]).map((tool) => (
                      <span
                        key={tool}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Status and Actions */}
              <div className="border-t border-gray-200 pt-4 mt-auto">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Tila:</span>
                  {getStatusBadge(isEnabled)}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleToggleSkill(skill)}
                    disabled={togglingSkill === skill.id}
                    variant={isEnabled ? 'danger' : 'primary'}
                    size="sm"
                    className="flex-1"
                  >
                    {togglingSkill === skill.id
                      ? '⏳ ...'
                      : isEnabled
                      ? '⚪ ' + t('common.disable')
                      : '🟢 ' + t('common.enable')}
                  </Button>

                  {isEnabled && (
                    <Button
                      onClick={() => handleConfigure(skill)}
                      variant="secondary"
                      size="sm"
                    >
                      ⚙️
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {skills.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">🛠️</div>
          <p className="text-lg font-medium">Ei skillejä saatavilla</p>
          <p className="text-sm">Yhtään skilliä ei ole vielä lisätty järjestelmään.</p>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && selectedSkill && (
        <SkillConfigModal
          skill={selectedSkill}
          onClose={() => {
            setShowConfigModal(false);
            setSelectedSkill(null);
          }}
          onSave={async (config) => {
            if (!activeOrg) return;
            try {
              await updateSkillConfig(selectedSkill.id, activeOrg.id, { config });
              await loadSkills();
              setShowConfigModal(false);
              setSelectedSkill(null);
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Failed to save configuration');
            }
          }}
        />
      )}
    </div>
  );
}

// Skill Configuration Modal Component
interface SkillConfigModalProps {
  skill: Skill;
  onClose: () => void;
  onSave: (config: Record<string, unknown>) => void;
}

function SkillConfigModal({ skill, onClose, onSave }: SkillConfigModalProps) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<Record<string, unknown>>(
    skill.orgConfig?.config || {}
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(config);
    setSaving(false);
  };

  // Simple form renderer based on schema
  const renderConfigFields = () => {
    const schema = skill.schema as Record<string, any>;
    if (!schema?.properties) {
      return (
        <p className="text-gray-500">
          Tällä skillillä ei ole konfiguroitavia asetuksia.
        </p>
      );
    }

    return Object.entries(schema.properties).map(([key, prop]: [string, any]) => (
      <div key={key} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {prop.description || key}
          {schema.required?.includes(key) && (
            <span className="text-red-500">*</span>
          )}
        </label>
        {prop.type === 'string' && (
          <input
            type="text"
            value={(config[key] as string) || ''}
            onChange={(e) => setConfig({ ...config, [key]: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={prop.description}
          />
        )}
        {prop.type === 'boolean' && (
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={(config[key] as boolean) || false}
              onChange={(e) => setConfig({ ...config, [key]: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm text-gray-600">{prop.description}</span>
          </label>
        )}
        {prop.type === 'number' && (
          <input
            type="number"
            value={(config[key] as number) || ''}
            onChange={(e) =>
              setConfig({ ...config, [key]: parseFloat(e.target.value) })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}
        {prop.enum && (
          <select
            value={(config[key] as string) || ''}
            onChange={(e) => setConfig({ ...config, [key]: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Valitse...</option>
            {prop.enum.map((option: string) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}
      </div>
    ));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-medium">
            ⚙️ {skill.name} - Konfiguraatio
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <p className="text-sm text-gray-600 mb-4">
            {skill.description}
          </p>
          {renderConfigFields()}
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Peruuta
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? '⏳ ' + t('common.saving') : '💾 ' + t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
